import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DATABASE } from "../../../../Config/firebase";
import { CAMPAIGN_PRESETS, clonePreset } from "../../../../Utils/campaignPresets";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const TemplateLibraryDialog = ({ open, onClose, onLoad, currentCampaign }) => {
  const [tab, setTab] = useState(0);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [search, setSearch] = useState("");

  const loadSaved = async () => {
    setLoading(true);
    try {
      const q = query(collection(DATABASE, "email_templates"), orderBy("updated_at", "desc"));
      const snap = await getDocs(q);
      setSaved(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      toast.error("Failed to load saved templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSaved();
      setSaveName(currentCampaign?.subject || "");
      setSearch("");
    }
  }, [open, currentCampaign?.subject]);

  const searchQuery = search.trim().toLowerCase();

  const visiblePresets = useMemo(() => {
    const sorted = [...CAMPAIGN_PRESETS].sort((a, b) =>
      a.label.localeCompare(b.label, "en", { sensitivity: "base" })
    );
    if (!searchQuery) return sorted;
    return sorted.filter((p) =>
      `${p.label} ${p.description || ""} ${p.subject || ""}`.toLowerCase().includes(searchQuery)
    );
  }, [searchQuery]);

  const visibleSaved = useMemo(() => {
    const sorted = [...saved].sort((a, b) =>
      (a.name || "Untitled").localeCompare(b.name || "Untitled", "en", { sensitivity: "base" })
    );
    if (!searchQuery) return sorted;
    return sorted.filter((tpl) =>
      `${tpl.name || ""} ${tpl.subject || ""}`.toLowerCase().includes(searchQuery)
    );
  }, [saved, searchQuery]);

  const handleLoadPreset = (preset) => {
    onLoad(clonePreset(preset));
    toast.info(`${preset.label} loaded`);
    onClose();
  };

  const handleLoadSaved = (tpl) => {
    onLoad({
      subject: tpl.subject || "",
      eyebrow: tpl.eyebrow || "CLIENT UPDATE",
      blocks: Array.isArray(tpl.blocks) ? JSON.parse(JSON.stringify(tpl.blocks)) : [],
    });
    toast.info(`${tpl.name || "Template"} loaded`);
    onClose();
  };

  const handleSaveAs = async () => {
    const name = saveName.trim();
    if (!name) {
      toast.warning("Give the template a name");
      return;
    }
    if (!currentCampaign?.blocks?.length) {
      toast.warning("Nothing to save");
      return;
    }
    try {
      await addDoc(collection(DATABASE, "email_templates"), {
        name,
        subject: currentCampaign.subject || "",
        eyebrow: currentCampaign.eyebrow || "CLIENT UPDATE",
        blocks: currentCampaign.blocks,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      toast.success("Template saved");
      setSaveName("");
      loadSaved();
      setTab(1);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Could not save template");
    }
  };

  const handleRename = async (tpl) => {
    const { value } = await Swal.fire({
      title: "Rename template",
      input: "text",
      inputValue: tpl.name || "",
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      confirmButtonText: "Save",
    });
    if (!value || !value.trim()) return;
    try {
      await updateDoc(doc(DATABASE, "email_templates", tpl.id), {
        name: value.trim(),
        updated_at: serverTimestamp(),
      });
      toast.success("Renamed");
      loadSaved();
    } catch (e) {
      toast.error(e.message || "Rename failed");
    }
  };

  const handleDelete = async (tpl) => {
    const confirm = await Swal.fire({
      title: "Delete template?",
      text: tpl.name || "Untitled",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#F52A2D",
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteDoc(doc(DATABASE, "email_templates", tpl.id));
      toast.success("Deleted");
      loadSaved();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Templates</DialogTitle>
      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search templates"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            "& .Mui-selected": { color: "#009658 !important" },
            "& .MuiTabs-indicator": { bgcolor: "#2af598" },
          }}
        >
          <Tab label="Presets" sx={{ textTransform: "none" }} />
          <Tab label={`My templates (${saved.length})`} sx={{ textTransform: "none" }} />
        </Tabs>
        <TabPanel value={tab} index={0}>
          {visiblePresets.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>No templates match.</Typography>
          ) : visiblePresets.map((p) => (
            <Box
              key={p.id}
              sx={{
                p: 1.5,
                mb: 1,
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{p.label}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{p.description}</Typography>
              </Box>
              <Button size="small" variant="contained" onClick={() => handleLoadPreset(p)} sx={{ bgcolor: "#2af598", color: "#0a1a12", "&:hover": { bgcolor: "#3dffb0" }, textTransform: "none" }}>
                Load
              </Button>
            </Box>
          ))}
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              label="Save current as"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
            />
            <Button variant="contained" onClick={handleSaveAs} sx={{ bgcolor: "#2af598", color: "#0a1a12", "&:hover": { bgcolor: "#3dffb0" }, textTransform: "none", whiteSpace: "nowrap" }}>
              Save
            </Button>
          </Box>
          {loading ? (
            <Typography sx={{ fontSize: 13 }}>Loading…</Typography>
          ) : visibleSaved.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              {saved.length === 0 ? "No saved templates yet." : "No templates match."}
            </Typography>
          ) : (
            visibleSaved.map((tpl) => (
              <Box
                key={tpl.id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{tpl.name || "Untitled"}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1 }}>{tpl.subject || "—"}</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button size="small" variant="contained" onClick={() => handleLoadSaved(tpl)} sx={{ bgcolor: "#2af598", color: "#0a1a12", "&:hover": { bgcolor: "#3dffb0" }, textTransform: "none" }}>
                    Load
                  </Button>
                  <Button size="small" onClick={() => handleRename(tpl)} sx={{ textTransform: "none" }}>
                    Rename
                  </Button>
                  <Button size="small" onClick={() => handleDelete(tpl)} sx={{ textTransform: "none", color: "#F52A2D" }}>
                    Delete
                  </Button>
                </Box>
              </Box>
            ))
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateLibraryDialog;
